import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportTypeVM } from 'ab-viewModels/fieldReport/fieldReport.viewModel';
import type FieldReportVM from 'ab-viewModels/fieldReport/fieldReport.viewModel';
import type WorkSummaryStatusVM from 'ab-viewModels/workOrder/workSummaryStatus.viewModel';

import type { UpsertFieldReportTypeRM } from 'ab-requestModels/fieldReport/fieldReport.requestModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import { FieldReportTypeStatusEnum } from '@acceligentllc/shared/enums/fieldReportTypeStatus';
import { WorkSummaryStatus } from '@acceligentllc/shared/enums/workSummaryStatus';

import type { RootState } from 'af-reducers';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { useToggle } from 'af-utils/react.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import TabNavigation from 'af-components/TabNavigation';

import ReportTypeModal from './Modals/ReportTypeModal';
import SelectWorkOrdersModal from './Modals/SelectWorkOrdersModal';

interface OwnProps {
	activeTabId: Nullable<number>;
	editReportTypes: (fieldReportTypes: UpsertFieldReportTypeRM[]) => void;
	fieldReportOrder: number[];
	fieldReportTypes: FieldReportVM['typeMap'];
	isPreview: boolean;
	lockedFieldReportTypes: number[];
	onTabChange: (typeId: number) => void;
	isWorkSummaryAccessible: boolean;
	workSummaryStatus: WorkSummaryStatusVM;
	jobId: number;
	workOrderId: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

export const WORK_SUMMARY_TAB_ID = 0;

interface DropdownOption {
	label: string;
	onClick: () => void;
}

interface Tab {
	id: number;
	label: string;
	status: FieldReportTypeStatusEnum;
	resolveCustomClassName?: (active: boolean) => string;
	generateTabKey?: () => string;
	workSummaryStatus?: WorkSummaryStatus;
	actionsOptions?: DropdownOption[];
}

const FieldReportTabs: React.FC = (props: Props) => {

	const {
		activeTabId,
		editReportTypes,
		fieldReportOrder,
		fieldReportTypes,
		isPreview,
		lockedFieldReportTypes,
		onTabChange,
		isWorkSummaryAccessible,
		workSummaryStatus,
		jobId,
		hasAllowAttachingPreviousFRsPermission,
		workOrderId,
	} = props;

	const [FRTypeForSelectWOsModal, setFRTypeForSelectWOsModal] = React.useState<Nullable<FieldReportTypeVM>>(null);

	const {
		value: showReportTypeModal,
		setToTrue: openReportTypeModal,
		setToFalse: closeReportTypeModal,
	} = useToggle(false);

	const closeSelectWOsModal = React.useCallback(() => {
		setFRTypeForSelectWOsModal(null);
	}, []);

	const resolveWorkSummaryTabIcon = React.useCallback((tabWorkSummaryStatus: Nullable<WorkSummaryStatus>) => {
		const className = bemElement('field-report__tab-navigation', 'work-summary-icon');

		switch (tabWorkSummaryStatus) {
			case WorkSummaryStatus.DRAFT: {
				return (
					<span className={bemBlock(className, { draft: true })}>
						<span className="icon-dot" />
					</span>
				);
			}
			case WorkSummaryStatus.IN_PROGRESS: {
				return (
					<span className={bemBlock(className, { 'in-progress': true })}>
						<span className="icon-dot" />
					</span>
				);
			}
			case WorkSummaryStatus.OUTDATED: {
				return (
					<span className={bemBlock(className, { outdated: true })}>
						<span className="icon-dot_outline" />
					</span>
				);
			}
			case WorkSummaryStatus.REVIEWED: {
				return (
					<span className={bemBlock(className, { reviewed: true })}>
						<span className="icon-dot" />
					</span>
				);
			}
			case WorkSummaryStatus.COMPLETED: {
				return (
					<span className={bemBlock(className, { completed: true })}>
						<span className="icon-dot" />
					</span>
				);
			}
			default: {
				return (
					<span className={bemBlock(className, { draft: true })}>
						<span className="icon-dot" />
					</span>
				);
			}
		}
	}, []);

	const workSummaryTab = React.useMemo<Tab>(() => {
		return {
			id: WORK_SUMMARY_TAB_ID,
			label: 'Work Summary',
			status: FieldReportTypeStatusEnum.NOT_FILLED,
			resolveCustomClassName: (active: boolean) => {
				return bemElement('tabs-navigation', 'work-summary-tab', {
					active,
				});
			},
			workSummaryStatus: workSummaryStatus as unknown as WorkSummaryStatus,
		};
	}, [workSummaryStatus]);

	const mapFieldReportTypesToTabs = React.useCallback((id: number, type: FieldReportTypeVM): Tab => {
		let status = FieldReportTypeStatusEnum.NOT_FILLED;
		if (type.hasRequiredField) {
			if (type.numberOfFilledRequiredFields === 0) {
				status = FieldReportTypeStatusEnum.NOT_FILLED;
			} else if (type.numberOfFilledRequiredFields === type.numberOfRequiredFields) {
				status = FieldReportTypeStatusEnum.COMPLETED;
			} else {
				status = FieldReportTypeStatusEnum.IN_PROGRESS;
			}
		}
		const tab: Tab = {
			id,
			label: type?.name,
			status,
			generateTabKey: () => `${id}-${status}`,
		};
		if (hasAllowAttachingPreviousFRsPermission && !DisabledFeatures.PreviousFieldReports) {
			tab.actionsOptions = [{
				label: 'Attach to Work Order', onClick: () => {
					setFRTypeForSelectWOsModal(type);
				},
			}];
		}
		return tab;
	}, [hasAllowAttachingPreviousFRsPermission]);

	const tabs = React.useMemo(() => {
		// FIXME: on the following FR, fieldReportOrder had a null in it. Check it out:
		// https://carylon.acceligent.com/company/NWMCC-BOS/fieldReport/workOrder/428842
		// After that, filter(Boolean) should be removed

		return (isWorkSummaryAccessible ? [workSummaryTab] : []).concat(
			fieldReportOrder
				.filter(Boolean)
				.map((_id) => mapFieldReportTypesToTabs(+_id, fieldReportTypes[_id]))
		);
	}, [fieldReportOrder, fieldReportTypes, isWorkSummaryAccessible, mapFieldReportTypesToTabs, workSummaryTab]);

	const resolveRequiredFieldsTabIcon = React.useCallback((status: Nullable<FieldReportTypeStatusEnum>) => {
		if (!status || status === FieldReportTypeStatusEnum.NOT_FILLED) {
			return;
		}
		const className = bemElement(
			'field-report__tab-navigation',
			'required-field-completion-icon',
			{
				'in-progress': status === FieldReportTypeStatusEnum.IN_PROGRESS,
				completed: status === FieldReportTypeStatusEnum.COMPLETED,
			}
		);
		return (
			<span className={className}>
				<span className="icon-dot" />
			</span>
		);
	}, []);

	const renderTab = React.useCallback((id: number) => {
		const tab = tabs.find((_tab) => _tab.id === id);

		if (!tab) {
			throw new Error(`tab ${id} not found`);
		}

		return (
			<div className="field-report__tab-navigation">
				{/*
					TODO: enable when validation starts working
					<Tooltip message={FieldReportTypeStatusEnumLabel[tab.status]}>
						<span className={FieldReportTypeStatusEnumIcon[tab.status]} />
					</Tooltip>
				*/}
				{tab.workSummaryStatus !== undefined ? resolveWorkSummaryTabIcon(tab.workSummaryStatus ?? null) : null}
				<span>{tab?.label}</span>
				{tab.workSummaryStatus === undefined ? resolveRequiredFieldsTabIcon(tab.status) : null}
			</div>
		);
	}, [resolveRequiredFieldsTabIcon, resolveWorkSummaryTabIcon, tabs]);

	return (
		<>
			<TabNavigation
				active={activeTabId}
				onClick={onTabChange}
				onEditTabs={!isPreview ? openReportTypeModal : undefined}
				renderLabel={renderTab}
				tabs={tabs}
			/>
			<ReportTypeModal
				closeModal={closeReportTypeModal}
				fieldReportOrder={fieldReportOrder}
				fieldReportTypes={fieldReportTypes}
				lockedFieldReportTypes={lockedFieldReportTypes}
				onSave={editReportTypes}
				showModal={showReportTypeModal}
			/>
			{
				!!FRTypeForSelectWOsModal &&
				<SelectWorkOrdersModal
					closeModal={closeSelectWOsModal}
					fieldReportTypeId={FRTypeForSelectWOsModal.id}
					jobId={jobId}
					showModal={true}
					workOrderId={workOrderId}
				/>
			}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const hasAllowAttachingPreviousFRsPermission = isAllowed(
		PagePermissions.COMPANY.FIELD_REPORT.ALLOW_ATTACHING_PREVIOUS_FR,
		permissions,
		isCompanyAdmin,
		role
	);

	return {
		hasAllowAttachingPreviousFRsPermission,
	};
}

const connector = connect(mapStateToProps, null);

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(FieldReportTabs);
